import React, { useMemo, useRef, useCallback } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "../../../css/DesignSystem/Inputs/DateField.css";

dayjs.extend(customParseFormat);

const DateField = ({
  id,
  name,
  label,
  value,
  onChange,
  placeholder = "DD/MM/YYYY",
  error = false,
  helperText = "",
  required = false,
  disabled = false,
  readOnly = false,
}) => {
  const inputRef = useRef(null);

  const inputId = useMemo(() => {
    if (id) return id;
    const safeLabel = String(label ?? "date")
      .trim()
      .replace(/\s+/g, "-")
      .toLowerCase();
    return `date-${safeLabel}`;
  }, [id, label]);

  const inputName = name || id || inputId;

  // Always feed <input type="date"> a YYYY-MM-DD string
  const inputValue = useMemo(() => {
    if (!value) return "";

    // Date instance
    if (value instanceof Date) {
      return Number.isNaN(value.getTime())
        ? ""
        : dayjs(value).format("YYYY-MM-DD");
    }

    // String values:
    if (typeof value === "string") {
      const v = value.trim();
      if (!v) return "";

      // Already in ISO-ish format YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;

      // Try common formats you used
      const parsed = dayjs(v, ["DD/MM/YYYY", "MM/YYYY", "YYYY"], true);
      return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
    }

    return "";
  }, [value]);

  const openPicker = useCallback(() => {
    if (disabled || readOnly) return;
    const el = inputRef.current;
    if (!el) return;

    el.focus();

    // Only works in some browsers (Chrome/Edge). Others will just focus.
    if (typeof el.showPicker === "function") {
      try {
        el.showPicker();
      } catch {
        // ignore – browser may block it
      }
    }
  }, [disabled, readOnly]);



  return (
    <div className={`ds-date-field ${disabled ? "is-disabled" : ""}`}>
      {label && (
        <label className="Content-Small" htmlFor={inputId}>
          {label}
          {required && (
            <span className="FP_Content_Semibold text-error">*</span>
          )}
        </label>
      )}

      <div
        className={[
          "ds-input-field-container",
          error ? "ds-input-field--error" : "",
          disabled ? "disabled" : "",
        ].join(" ")}
        onClick={openPicker}
      >
        <div className="ds-input-wrapper">
          <input
            ref={inputRef}
            id={inputId}
            name={inputName}
            type="date"
            value={inputValue}
            className="Content ds-input ds-date-input"
            onChange={onChange}
            disabled={disabled}
            readOnly={readOnly}
            // placeholder is ignored by most browsers for type="date", but harmless
            placeholder={placeholder}
          />

          <button
            type="button"
            className="ds-input-icon calendar-icon"
            aria-label="Open calendar"
            onClick={openPicker}
            disabled={disabled || readOnly}
          >
            <i className="fas fa-calendar" aria-hidden="true" />
          </button>
          {/* {!disabled && (
          )} */}
        </div>
      </div>

      {/* {!!helperText && (
        <div
          className={`ds-input-helper ${error ? "ds-input-helper--error" : ""}`}
        >
          {helperText}
        </div>
      )} */}
    </div>
  );
};

export default DateField;
